import {
  Accordion,
  AccordionDetails,
  Box,
  Button,
  Button as LoadingButton,
  FormControl,
  FormHelperText,
  IconButton,
  ListItemButton,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CakeIcon from "@mui/icons-material/Cake";
import theme from "../theme";

const STACK_SYNC_BUTTON_BELOW = `@media (max-width: 1399px)`;

export const AddPatientInfoLayout = styled(Box)`
  flex-direction: column;
  margin-top: 20px;
`;

export const FormControlLayout = styled(FormControl)`
  padding: 16px;
  display: flex;
`;

export const SearchField = styled(Box)`
  margin-bottom: 20px;
`;

// At narrow widths the MRN input + Sync button can't share a row inside the
// 360 px chat info drawer — stack them vertically
export const HorizontalLayout = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${STACK_SYNC_BUTTON_BELOW} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const VerticalLayout = styled(Box)`
  display: flex;
  flex-direction: column;
`;

export const SyncButton = styled(LoadingButton)`
  margin-left: 16px;
  width: 225px;
  padding: 8px 16px;

  ${STACK_SYNC_BUTTON_BELOW} {
    width: 100%;
    margin-left: 0;
    margin-top: 8px;
  }
`;

export const InputField = styled(TextField)`
  width: 100%;
  margin-bottom: 20px;
`;

export const MRNInputField = styled(TextField)`
  width: 100%;

  & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(0, 0, 0, 0.23);
  }
`;

export const InputAddInfoField = styled(TextField)`
  width: 100%;
  margin-bottom: 10px;
`;

export const InputFieldAccordion = styled(TextField)`
  margin-bottom: 12px;
`;

export const AccordionDetailsLayout = styled(AccordionDetails)`
  display: flex;
  justify-content: space-between;
`;

export const AccordionDetailsAddInfo = styled(AccordionDetails)`
  padding: 0 16px 12px;
`;

export const RecentMRNAccordion = styled(Accordion)`
  border-radius: 4px;
  margin-bottom: 20px;

  &::before {
    display: none;
  }

  &.Mui-expanded {
    margin: 0 0 20px;
  }
`;

export const RecentMRNSummaryContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RecentMRNDetails = styled(AccordionDetails)`
  padding: 0 8px 8px;
  max-height: 280px;
  overflow-y: auto;
`;

export const RecentMRNItemButton = styled(ListItemButton)`
  padding: 8px;
  border-radius: 8px;
  gap: 4px;
`;

export const RecentMRNPrimary = styled(Box)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

export const RecentMRNName = styled(Typography)`
  font-weight: 500;
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RecentMRNLoader = styled(Box)`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;

export const RecentMRNMeta = styled(Typography)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 14px;
  color: ${theme.palette.text.secondary};
`;

export const RecentMRNCakeIcon = styled(CakeIcon)`
  font-size: 16px;
  color: ${theme.palette.text.secondary};
`;

export const FormControlRadioGroup = styled(FormControl)`
  margin-top: 20px;
`;

export const AddInfoLayout = styled(Box)`
  margin-bottom: 10px;
`;

export const FieldTitleText = styled(Typography)`
  font-weight: bold;
  margin-bottom: 16px;
`;

export const FieldHintContainer = styled(Box)`
  display: flex;
`;

export const FieldHintText = styled(Typography)`
  font-size: 14px;
  margin-top: -14px;
  font-style: italic;
`;

export const FieldHintLabel = styled(Typography)`
  font-size: 14px;
  margin-top: -14px;
  font-style: italic;
  color: rgb(0, 0, 0, 0.42);
  margin-right: 10px;
`;

export const PhoneNumberLayout = styled(Box)`
  display: flex;
  align-items: flex-end;
  width: 100%;
`;

export const CountryCodeSelect = styled("select")`
  width: 40%;
  border: none;
  height: 37px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.42);
  margin-right: 13px;
  font-size: 1rem;
  color: rgb(0, 0, 0, 0.87);
  outline: none;
`;

export const PhoneNumberInput = styled(TextField)`
  width: 60%;
`;

export const HelperText = styled(FormHelperText)`
  color: ${theme.palette.error.main};
  margin-bottom: 20px;
`;

export const SubmitButtonWrapper = styled(Box)`
  position: sticky;
  bottom: 0;
  background-color: white;
  z-index: 1;
`;

export const SubmitButton = styled(LoadingButton)`
  margin: 8px 0 8px 16px;
`;

export const SetForthButton = styled(LoadingButton)`
  margin: 8px 0 8px 16px;
  width: 150px;
`;

export const WordLimit = styled(Typography)`
  font-size: x-small;
  color: ${theme.palette.grey[600]};
  align-self: flex-end;
`;

export const AboutInputField = styled(TextField)`
  width: 100%;
  margin-bottom: 5px;
`;

export const ReferralUpload = styled(Box)`
  width: 80px;
  height: 80px;
`;

export const ReferralButton = styled(Button)`
  height: 100%;
  background: ${theme.palette.divider};
`;

export const ReferralFilesWrapper = styled(Box)`
  margin-top: 20px;
`;

export const ReferralFilesContainer = styled(Box)``;
export const ReferralFileItem = styled(Box)`
  display: flex;
  align-items: center;
  padding: 6px;
  border: 1px solid ${theme.palette.divider};
  margin-bottom: 10px;
  border-radius: 4px;
  cursor: pointer;
  position: relative;

  &:hover {
    box-shadow: 0 0 2px ${theme.palette.divider};
  }
`;

export const ReferralMediaIcon = styled(Box)`
  flex: 0 0 auto;
  width: 50px;
  height: 50px;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 16px;
  box-shadow: 0 0 2px ${theme.palette.grey[700]};
  padding: 5px;
`;

export const ReferralImage = styled("img")`
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

export const ReferralFileInfo = styled(Box)`
  width: 100%;
`;

export const ReferralCaptionInput = styled("input")`
  outline: none;
  background-color: initial;
  border: none;
  border-bottom: 1px solid ${theme.palette.divider};
  padding: 4px 0;
  width: 100%;
  font-weight: 500;
  font-size: 14px;
`;

export const ReferralRemoveIcon = styled(IconButton)`
  position: absolute;
  top: -14px;
  right: -13px;
  box-shadow: 0 0 3px 0 ${theme.palette.grey[700]};
  padding: 0;
`;

export const ReferralFileItemButton = styled(Button)`
  display: flex;
  align-items: center;
  padding: 6px;
  border: 1px solid ${theme.palette.divider};
  margin-bottom: 10px;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  width: 100%;
  justify-content: flex-start;

  &:hover {
    box-shadow: 0 0 2px ${theme.palette.divider};
  }
`;
